/*
 * VAJA · section behaviour. Loaded once for the whole site (snippets/stylesheets.liquid).
 * Never put <script> tags inside section markup: the theme editor swaps section HTML
 * on every change and does not run inline scripts again. Custom elements defined here
 * upgrade automatically whenever a section is added, re-rendered or re-ordered.
 */

class VajaColourStory extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }

  #onClick = (event) => {
    const button = event.target instanceof Element ? event.target.closest('.vaja-swatch') : null;
    if (button instanceof HTMLElement && this.contains(button)) this.select(button);
  };

  /** @param {HTMLElement} button */
  select(button) {
    const index = button.dataset.index;

    this.querySelectorAll('.vaja-swatch').forEach((swatch) => {
      swatch.setAttribute('aria-pressed', String(swatch === button));
    });

    this.querySelectorAll('[data-frame]').forEach((frame) => {
      const active = frame instanceof HTMLElement && frame.dataset.frame === index;
      frame.toggleAttribute('hidden', !active);
      frame.classList.toggle('is-active', active);
    });

    const price = this.querySelector('[data-price-target]');
    if (price) price.textContent = button.dataset.price || '';

    const cta = this.querySelector('[data-cta-target]');
    if (cta instanceof HTMLAnchorElement) {
      if (button.dataset.url) cta.href = button.dataset.url;
      if (button.dataset.cta) cta.textContent = button.dataset.cta;
    }
  }
}

if (!customElements.get('vaja-colour-story')) {
  customElements.define('vaja-colour-story', VajaColourStory);
}

/* Video: muted preview loop; "Bekijk de video" restarts it with sound and controls. */
class VajaVideo extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    const video = this.querySelector('video');
    if (video && video.autoplay && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      video.autoplay = false;
      video.pause();
    }
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }

  #onClick = (event) => {
    const trigger = event.target instanceof Element ? event.target.closest('[data-play]') : null;
    if (trigger && this.contains(trigger)) this.play();
  };

  play() {
    const video = this.querySelector('video');
    if (!video) return;
    this.classList.add('is-playing');
    video.loop = false;
    video.controls = true;
    video.muted = false;
    video.currentTime = 0;
    video.play()?.catch(() => {
      video.muted = true;
      video.play()?.catch(() => {});
    });
    video.focus({ preventScroll: true });
  }
}

if (!customElements.get('vaja-video')) {
  customElements.define('vaja-video', VajaVideo);
}

/*
 * Variant grid: quick add straight into Horizon's cart drawer.
 * 1. POST /cart/add.js as JSON (Shopify's documented Ajax API).
 * 2. Tell Horizon via CartLinesUpdateEvent (@shopify/events) so the drawer,
 *    cart items and bubble refresh and the drawer opens.
 * Success is decided by the HTTP status only. Any error in the Horizon hand-off
 * is logged but never turns a successful add into an error message.
 * Errors stay on the page, under the button. Look for "[vaja-quick-add]" in the console.
 * Note: Horizon declares `Theme` with const, so it is not on window.
 */
const vajaEvents = import('@shopify/events').catch((error) => {
  console.warn('[vaja-quick-add] @shopify/events not available', error);
  return null;
});
const vajaRoot = () => /** @type {any} */ (window).Shopify?.routes?.root || '/';

document.addEventListener('submit', async (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || !form.matches('[data-vaja-quick-add]')) return;
  event.preventDefault();

  const button = form.querySelector('button[type="submit"]');
  if (!(button instanceof HTMLButtonElement) || button.disabled || button.getAttribute('aria-busy') === 'true') return;

  const label = button.querySelector('[data-btn-label]');
  const errorTarget = form.querySelector('[data-error-target]');
  const original = label?.textContent ?? '';
  const showError = (message) => {
    if (!(errorTarget instanceof HTMLElement)) return;
    errorTarget.textContent = message || form.dataset.error || '';
    errorTarget.hidden = false;
  };
  if (errorTarget instanceof HTMLElement) errorTarget.hidden = true;
  button.setAttribute('aria-busy', 'true');

  const variantId = Number(new FormData(form).get('id'));
  const quantity = Number(new FormData(form).get('quantity')) || 1;
  const root = vajaRoot();
  const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
    .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : ''))
    .filter(Boolean);

  const mod = await vajaEvents;
  const CartLinesUpdateEvent = mod?.CartLinesUpdateEvent;

  /** @type {{promise: Promise<any>, resolve: Function, reject: Function} | null} */
  let deferred = null;
  if (CartLinesUpdateEvent) {
    try {
      deferred = CartLinesUpdateEvent.createPromise();
      deferred.promise.catch(() => {});
      form.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: [{ merchandiseId: String(variantId), quantity }],
          promise: deferred.promise,
        })
      );
    } catch (error) {
      console.warn('[vaja-quick-add] could not dispatch CartLinesUpdateEvent', error);
      deferred = null;
    }
  }

  /** @type {Response | null} */
  let response = null;
  let data = null;
  try {
    const body = { items: [{ id: variantId, quantity }] };
    if (sectionIds.length) body.sections = sectionIds.join(',');
    response = await fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(body),
    });
    data = await response.json().catch(() => null);
  } catch (error) {
    console.error('[vaja-quick-add] request failed', error);
  }

  const ok = Boolean(response?.ok);
  if (!ok) console.warn('[vaja-quick-add] add rejected', response?.status, data);

  let cart = null;
  try {
    cart = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
  } catch (error) {
    console.warn('[vaja-quick-add] could not read cart', error);
  }

  if (deferred && CartLinesUpdateEvent) {
    try {
      deferred.resolve({
        cart: cart ? CartLinesUpdateEvent.createCartFromAjaxResponse(cart) : undefined,
        detail: {
          didError: !ok,
          items: cart?.items,
          source: 'vaja-variant-grid',
          sourceId: form.id,
          itemCount: quantity,
          sections: ok ? data?.sections : undefined,
        },
      });
    } catch (error) {
      console.warn('[vaja-quick-add] cart drawer hand-off failed', error);
      deferred.resolve({ detail: { didError: !ok, itemCount: quantity, source: 'vaja-variant-grid', sourceId: form.id } });
    }
  }

  button.removeAttribute('aria-busy');

  if (!ok) {
    showError(data?.description || data?.message);
    return;
  }

  if (!deferred) {
    window.location.href = `${root}cart`;
    return;
  }

  if (label) {
    label.textContent = form.dataset.added || original;
    window.setTimeout(() => {
      label.textContent = original;
    }, 2000);
  }
});

/* Theme editor: selecting a block shows it in the preview. */
document.addEventListener('shopify:block:select', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const swatch = target.closest('.vaja-swatch');
  const story = swatch?.closest('vaja-colour-story');
  if (swatch instanceof HTMLElement && story instanceof VajaColourStory) story.select(swatch);

  if (target instanceof HTMLDetailsElement && target.matches('.vaja-faq__item')) target.open = true;
});

/* Footer: "Cookie-instellingen" opens Shopify's cookie banner preferences. */
document.addEventListener('click', (event) => {
  const trigger = event.target instanceof Element ? event.target.closest('[data-vaja-cookie-settings]') : null;
  if (!trigger) return;

  event.preventDefault();
  const banner = /** @type {any} */ (window).privacyBanner;
  if (banner && typeof banner.showPreferences === 'function') {
    banner.showPreferences();
  } else {
    window.location.href = '/policies/privacy-policy';
  }
});